import React, { useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import CampaignForm from './CampaignForm'
import SkeletonForm from '../../../../components/Form/Skeleton'
import LineItemForm from './LineItemForm'
import AdTikTokCreateForm from '../../AdForms/AdTikTokCreate/AdTikTokCreateForm'

import { useAccessibleTikTokIdentities } from '../../../../features/hooks/useAccessibleTikTokIdentities'
import useContinuousForm from '../../../../hooks/formHooks/useContinuousForm'
import useRedirectFormDrawer from '../../../../features/components/FormDrawerWrapper/useRedirectFormDrawer'

import { clearTikTokPixels, getTikTokPixels } from '../../../../modules/actions/tikTokConstants'
import { clearAds } from '../../../../modules/actions/ads'
import { selectedTikTokAdAccountSelector } from '../../../../modules/selectors/adAccounts'
import { tikTokPixelsErrorSelector, tikTokPixelsWasLoadedSelector } from '../../../../modules/selectors/tikTokConstants'
import { tiktokRightUploadAdDataSelector } from '../../../../modules/selectors/rights'

import { AD_FORM, CAMPAIGN_FORM, LINE_ITEM_FORM } from '../index'
import { ROUTE_PARAMS, ROUTES } from '../../../../constants/routes'
import { MODAL_TYPE } from '../../../../constants/other'

const UploadTikTokAdCreativeForm = ({ setRedirectionParams }) => {
  const dispatch = useDispatch()

  const { id: selectedTikTokAdAccountId } = useSelector(selectedTikTokAdAccountSelector)
  const { campaign, lineItem } = useSelector(tiktokRightUploadAdDataSelector)
  const tikTokPixelsWasLoaded = useSelector(tikTokPixelsWasLoadedSelector)
  const tikTokPixelsError = useSelector(tikTokPixelsErrorSelector)
  const tikTokPixelsDidLoading = tikTokPixelsWasLoaded || tikTokPixelsError

  const { showWarningMessage, identitiesIsLoading, TikTokIdentitiesWarningMessage } = useAccessibleTikTokIdentities()

  const { activatedForm, setActivatedForm } = useContinuousForm({
    initialForm: CAMPAIGN_FORM
  })

  const handleSuccessRedirect = useRedirectFormDrawer(
    `${ROUTES.rightsSummary}?message=${MODAL_TYPE.rightTikTokAdUploaded}`
  )

  const onSuccessAdFormSubmit = useCallback(() => {
    setRedirectionParams({
      [ROUTE_PARAMS.campaignId]: campaign.id,
      [ROUTE_PARAMS.lineItemId]: lineItem.id
    })

    dispatch(clearAds())
    handleSuccessRedirect()
  }, [dispatch, campaign.id, lineItem.id, handleSuccessRedirect, setRedirectionParams])

  const formProgressItems = useMemo(
    () => [
      {
        label: 'Campaign',
        isActive: activatedForm === CAMPAIGN_FORM,
        isSuccess: activatedForm === LINE_ITEM_FORM || activatedForm === AD_FORM
      },
      {
        label: 'Ad set',
        isActive: activatedForm === LINE_ITEM_FORM,
        isSuccess: activatedForm === AD_FORM
      },
      {
        label: 'Creative assets',
        isActive: activatedForm === AD_FORM,
        isSuccess: false
      }
    ],
    [activatedForm]
  )

  const getActivatedForm = useCallback(() => {
    switch (activatedForm) {
      case AD_FORM:
        return <AdTikTokCreateForm formProgressItems={formProgressItems} onSuccessSubmit={onSuccessAdFormSubmit} />
      case LINE_ITEM_FORM:
        return (
          <LineItemForm
            formProgressItems={formProgressItems}
            onSuccessSubmit={() => {
              setActivatedForm(AD_FORM)
            }}
          />
        )
      case CAMPAIGN_FORM:
      default:
        return (
          <CampaignForm
            formProgressItems={formProgressItems}
            onSuccessSubmit={() => {
              setActivatedForm(LINE_ITEM_FORM)
            }}
          />
        )
    }
  }, [activatedForm, formProgressItems, onSuccessAdFormSubmit, setActivatedForm])

  useEffect(() => {
    dispatch(getTikTokPixels({ account: selectedTikTokAdAccountId, order_by: 'LATEST_CREATE' }))

    return () => {
      dispatch(clearTikTokPixels())
    }
  }, [dispatch, selectedTikTokAdAccountId])

  if (identitiesIsLoading || !tikTokPixelsDidLoading) {
    return <SkeletonForm />
  } else if (showWarningMessage) {
    return TikTokIdentitiesWarningMessage
  }

  return <>{getActivatedForm()}</>
}

export default UploadTikTokAdCreativeForm
